import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Trash2, Key, Plus, Search } from 'lucide-react';
import { User, UserRole, EntityStatus } from '../../types/entities';
import { userService } from '../../services/userService';
import { usePermission } from '../../hooks/usePermission';
import UserFormModal from './UserFormModal';
import ResetPasswordModal from './ResetPasswordModal';
import toast from 'react-hot-toast';

const UserListPage: React.FC = () => {
  const { t } = useTranslation();
  const { can } = usePermission();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [statusFilter, setStatusFilter] = useState<EntityStatus | ''>('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    loadUsers();
  }, [roleFilter, statusFilter, searchTerm]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = {
        role: roleFilter || undefined,
        status: statusFilter || undefined,
        search: searchTerm || undefined,
      };
      const data = await userService.list(params);
      setUsers(data);
    } catch (err) {
      toast.error(t('users.error.loadFailed'));
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setFormModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setFormModalOpen(true);
  };

  const handleResetPassword = (user: User) => {
    setSelectedUser(user);
    setResetPasswordModalOpen(true);
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(t('users.confirmDelete', { username: user.username }))) {
      return;
    }

    try {
      await userService.delete(user.id);
      toast.success(t('common.deleted'));
      await loadUsers();
    } catch (err) {
      toast.error(t('users.error.deleteFailed'));
      console.error('Error deleting user:', err);
    }
  };

  const handleFormSuccess = () => {
    setFormModalOpen(false);
    loadUsers();
  };

  const handleResetPasswordSuccess = () => {
    setResetPasswordModalOpen(false);
    toast.success(t('common.success'));
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-red-100 text-red-700';
      case 'OPERATIONS_MANAGER':
        return 'bg-orange-100 text-orange-700';
      case 'ACCOUNTANT':
        return 'bg-blue-100 text-blue-700';
      case 'DRIVER':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status: EntityStatus) => {
    return status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return <div className="text-center py-10">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('users.title')}</h1>
        {can('user:create') && (
          <button
            onClick={handleCreateUser}
            className="btn-primary flex items-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('users.addUser')}
          </button>
        )}
      </div>

      <div className="card">
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder={t('users.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as UserRole | '')}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t('common.all')} {t('users.role')}</option>
            <option value="ADMIN">{t('roles.ADMIN')}</option>
            <option value="OPERATIONS_MANAGER">{t('roles.OPERATIONS_MANAGER')}</option>
            <option value="ACCOUNTANT">{t('roles.ACCOUNTANT')}</option>
            <option value="DRIVER">{t('roles.DRIVER')}</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as EntityStatus | '')}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t('common.all')} {t('users.status')}</option>
            <option value="ACTIVE">{t('status.ACTIVE')}</option>
            <option value="INACTIVE">{t('status.INACTIVE')}</option>
          </select>
        </div>

        <table className="w-full">
          <thead className="border-b">
            <tr className="text-left">
              <th className="pb-3 table-header">{t('users.username')}</th>
              <th className="pb-3 table-header">{t('users.email')}</th>
              <th className="pb-3 table-header">{t('users.role')}</th>
              <th className="pb-3 table-header">{t('users.status')}</th>
              <th className="pb-3 table-header">{t('users.linkedDriver')}</th>
              <th className="pb-3 table-header">{t('users.lastLogin')}</th>
              <th className="pb-3 table-header text-right">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-gray-500">
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className="border-b last:border-0">
                  <td className="py-3 font-medium">{user.username}</td>
                  <td className="py-3">{user.email || '-'}</td>
                  <td className="py-3">
                    <span className={`px-2 py-1 rounded text-sm ${getRoleColor(user.role)}`}>
                      {t(`roles.${user.role}`)}
                    </span>
                  </td>
                  <td className="py-3">
                    <span className={`px-2 py-1 rounded text-sm ${getStatusColor(user.status)}`}>
                      {t(`status.${user.status}`)}
                    </span>
                  </td>
                  <td className="py-3">{user.driver?.name || '-'}</td>
                  <td className="py-3">{formatDate(user.lastLoginAt)}</td>
                  <td className="py-3">
                    <div className="flex justify-end space-x-2">
                      {can('user:update') && (
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title={t('common.edit')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {can('user:reset_password') && (
                        <button
                          onClick={() => handleResetPassword(user)}
                          className="p-2 text-orange-600 hover:bg-orange-50 rounded"
                          title={t('users.resetPassword')}
                        >
                          <Key className="w-4 h-4" />
                        </button>
                      )}
                      {can('user:delete') && (
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title={t('common.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {formModalOpen && (
        <UserFormModal
          open={formModalOpen}
          onClose={() => setFormModalOpen(false)}
          onSuccess={handleFormSuccess}
          user={selectedUser}
        />
      )}

      {resetPasswordModalOpen && selectedUser && (
        <ResetPasswordModal
          open={resetPasswordModalOpen}
          onClose={() => setResetPasswordModalOpen(false)}
          onSuccess={handleResetPasswordSuccess}
          user={selectedUser}
        />
      )}
    </div>
  );
};

export default UserListPage;